import {
  SUBSTEP_TYPE,
  OPTIONAL_KEYS,
  isDisclosureRadioItems,
  isBlankOrNa,
  isValidFssaiLicenseNumber,
  isValidShippingPincode,
  needsCinForOwnership,
} from "utils/constants/onboarding";
import {
  buildPrimitiveValueByKey,
  getSelectedDisclosureItem,
  hasTodoPrimitives,
  isNoDisclosureItem,
} from "./substeps/todoListUtils";

// Cross-field required rules live here. When backend adds a new conditional
// requirement, add it here and both the UI star + Continue gate will follow.
export const isSpecialRequired = (substep, valueByKey = {}) => {
  const ownershipType = valueByKey.owner_ship_type_input ?? "";
  const cinNumber = valueByKey.company_incorporation_number ?? "";
  const gstNumber = valueByKey.gst_number_input ?? "";

  switch (substep?.key) {
    case "company_incorporation_number":
      return needsCinForOwnership(ownershipType);
    case "incorporation_document":
      return needsCinForOwnership(ownershipType) || !isBlankOrNa(cinNumber);
    case "gst_certificate_upload":
      return !isBlankOrNa(gstNumber);
    default:
      return false;
  }
};

export const isSubstepOptional = (substep, valueByKey = {}) => {
  if (!substep) return true;
  if (OPTIONAL_KEYS.has(substep.key)) return true;
  if (isSpecialRequired(substep, valueByKey)) return false;

  switch (substep.substep_type) {
    case SUBSTEP_TYPE.TEXT_INPUT:
      return (substep.input_text?.min_length ?? 1) === 0;
    case SUBSTEP_TYPE.IMAGE_INPUT:
      return (substep.input_image?.min_images ?? 1) === 0;
    default:
      return false;
  }
};

export const isSubstepRequired = (substep, valueByKey = {}) =>
  !isSubstepOptional(substep, valueByKey);

export const getStringValue = (values = {}, substepId) =>
  (values[substepId]?.string_value ?? "").toString().trim();

export const getImageValues = (values = {}, substepId) =>
  values[substepId]?.string_list_value ?? [];

const isContactVerified = (substep, values) => {
  const val = values[substep.substep_id];
  return !!val?.verified && !!getStringValue(values, substep.substep_id);
};

const getTodoValueContext = (items, values, valueByKey) => {
  const primitiveValueByKey = buildPrimitiveValueByKey(items, values);
  return {
    primitiveValueByKey,
    combinedValueByKey: {
      ...valueByKey,
      ...primitiveValueByKey,
    },
  };
};

export const isSubstepFilled = (substep, values = {}, valueByKey = {}) => {
  if (!substep) return true;

  // FSSAI is optional, but a supplied value must be a complete 14-digit number.
  // Check this before the generic optional-field shortcut.
  if (substep.key === "fssai_license_number") {
    return isValidFssaiLicenseNumber(getStringValue(values, substep.substep_id));
  }

  if (substep.key === "shipping_pincode") {
    const pincode = getStringValue(values, substep.substep_id);
    return (!pincode && isSubstepOptional(substep, valueByKey)) ||
      isValidShippingPincode(pincode);
  }

  if (isSubstepOptional(substep, valueByKey)) return true;

  if (substep.key === "contact_phone" || substep.key === "contact_email") {
    return isContactVerified(substep, values);
  }

  switch (substep.substep_type) {
    case SUBSTEP_TYPE.TEXT_INPUT:
      if (substep.key === "company_incorporation_number" && isSpecialRequired(substep, valueByKey)) {
        return !isBlankOrNa(getStringValue(values, substep.substep_id));
      }
      return !!getStringValue(values, substep.substep_id);

    case SUBSTEP_TYPE.IMAGE_INPUT: {
      const minImages = isSpecialRequired(substep, valueByKey)
        ? Math.max(1, substep.input_image?.min_images ?? 1)
        : substep.input_image?.min_images ?? 1;
      return getImageValues(values, substep.substep_id).length >= minImages;
    }

    case SUBSTEP_TYPE.TODO_LIST:
      return isTodoListFilled(substep, values, valueByKey);

    default:
      return true;
  }
};

export const isPrimitiveRequired = (prim, valueByKey = {}) =>
  isSubstepRequired(prim, valueByKey);

export const isPrimitiveSatisfied = (prim, values = {}, valueByKey = {}) =>
  !isPrimitiveRequired(prim, valueByKey) || isSubstepFilled(prim, values, valueByKey);

const isPureRadioTodoFilled = (items, values) => {
  const checkedItem = items.find((item) => !!values[item.substep_id]?.bool_value);
  return !!checkedItem && isNoDisclosureItem(checkedItem);
};

const isDisclosureTodoFilled = (items, values, valueByKey) => {
  const selectedItem = getSelectedDisclosureItem(items, values);
  if (!selectedItem) return false;
  return (selectedItem.primitive_inputs ?? []).every((prim) =>
    isSubstepFilled(prim, values, valueByKey)
  );
};

const isFormGroupTodoFilled = (items, values, valueByKey) => {
  const { primitiveValueByKey, combinedValueByKey } = getTodoValueContext(items, values, valueByKey);
  const gstIsNa = (primitiveValueByKey.gst_number_input ?? "").toUpperCase() === "NA";

  for (const item of items) {
    for (const prim of item.primitive_inputs ?? []) {
      if (prim.key === "gst_certificate_upload" && gstIsNa) continue;
      if (!isSubstepFilled(prim, values, combinedValueByKey)) return false;
    }
  }
  return true;
};

const isTodoListFilled = (substep, values, valueByKey) => {
  const items = substep.input_todo_list?.items ?? [];
  if (!items.length) return true;
  if (!hasTodoPrimitives(items)) return isPureRadioTodoFilled(items, values);
  if (isDisclosureRadioItems(items)) return isDisclosureTodoFilled(items, values, valueByKey);
  return isFormGroupTodoFilled(items, values, valueByKey);
};
